import { API_BASE_URL } from '../constant/constant';

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    let errorMessage = 'Payment failed';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.message || errorMessage;
    } catch (parseError) {
      // If we can't parse the error response, use a generic message
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }
  return response.json();
};

// Initialize Paystack payment
export const initializePaystackPayment = async (plan, token) => {
  try {
    const requestBody = {
      plan: plan.paystackPlanId || plan.id || plan.stripePriceId || plan.name || plan,
      amount: plan.amount || (plan.price ? parseFloat(plan.price.replace(/[^0-9.]/g, '')) * 100 : 0), // Convert to kobo
      currency: 'NGN',
      email: plan.email || '', // User email will be provided by the form
      metadata: {
        plan_name: plan.name,
        tokens: plan.tokens || 0,
        user_id: plan.userId || '',
        discount_code_id: plan.discountCodeId || null
      }
    };

    if (plan.discountCode) {
      requestBody.discountCode = plan.discountCode;
    }

    const response = await fetch(`${API_BASE_URL}/payments/paystack/initialize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Error initializing Paystack payment:', error);
    throw new Error('Failed to initialize payment. Please check your connection and try again.');
  }
};

// Verify Paystack payment
export const verifyPaystackPayment = async (reference, token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/paystack/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ reference }),
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Error verifying Paystack payment:', error);
    throw new Error('Failed to verify payment. Please contact support if the payment was successful.');
  }
};

// Get Paystack pricing plans (converted to NGN)
export const getPaystackPaymentPlans = async (token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/paystack/pricing-plans`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await handleResponse(response);
    return data.plans || data; // Handle both wrapped and unwrapped responses
  } catch (error) {
    console.error('Error fetching Paystack pricing plans:', error);
    throw new Error('Failed to load pricing plans. Please try again later.');
  }
};

// Convert USD to NGN (approximate rate, should be updated from API)
export const convertUSDToNGN = (usdAmount) => {
  const USD_TO_NGN_RATE = 1500; // Approximate rate, should be fetched from API
  return Math.round(usdAmount * USD_TO_NGN_RATE);
};

// Format amount for Paystack (in kobo)
export const formatAmountForPaystack = (amount) => {
  return Math.round(amount * 100); // Convert to kobo
};

// Format amount for display in NGN
export const formatNGNAmount = (amount) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};
